import { useTranslation } from "react-i18next";
import { useMatches } from "react-router-dom";
import type { RouteObject, UIMatch } from "react-router-dom";

import NotFound from "../pages/NotFound";

import {
  About,
  Create,
  FAQ,
  Main,
  MapBrowser,
  ServicePageStandalone,
} from "./LazyPages";
import { PrivatePage } from "./PrivatePage";

export const appRoutes = {
  mainPage: "/",
  mapPage: "/map",
  faqPage: "/faq",
  servicePage: "/geoservices",
  create: "/create",
  edit: "/edit",
  aboutPage: "/about",
  other: "/404",
} as const;

export type AppRouteKey = keyof typeof appRoutes;

export function getAppRoute(key: AppRouteKey): string {
  return appRoutes[key] || appRoutes.other;
}

interface RouteHandle {
  title?: string;
  inMenu?: boolean;
}

export function useAppMatches() {
  return useMatches() as UIMatch<unknown, RouteHandle>[];
}

export function useAppRoutes() {
  const { t } = useTranslation();

  const routesConfig: RouteObject[] = [
    {
      id: "main",
      path: appRoutes.mainPage,
      element: <Main />,
      handle: {
        title: t("navigation.main"),
        inMenu: true,
      } satisfies RouteHandle,
    },
    {
      id: "map-browser",
      path: appRoutes.mapPage,
      element: <MapBrowser />,
      handle: {
        title: t("navigation.map"),
        inMenu: true,
      } satisfies RouteHandle,
    },
    {
      id: "service-list",
      path: appRoutes.servicePage,
      element: <Main />,
    },
    {
      id: "service-detail",
      path: `${appRoutes.servicePage}/:id`,
      element: <ServicePageStandalone />,
    },
    {
      id: "create",
      path: appRoutes.create,

      element: (
        <PrivatePage>
          <Create />
        </PrivatePage>
      ),
    },
    {
      id: "edit",
      path: appRoutes.edit,
      children: [
        {
          path: ":id",
          element: <Create />,
        },
      ],
    },
    {
      id: "about",
      path: appRoutes.aboutPage,
      element: <About />,
      handle: {
        title: t("navigation.about"),
        inMenu: true,
      } satisfies RouteHandle,
    },
    {
      id: "faq",
      path: appRoutes.faqPage,
      element: <FAQ />,
      handle: {
        title: t("navigation.faq"),
        inMenu: true,
      } satisfies RouteHandle,
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ];
  return routesConfig;
}
